/**
 * Team & Access — one page, one question: who is involved with this program,
 * and what can they touch?
 *
 * ONE deduped, people-first table (layout-spec archetype B). A person appears
 * once; their relationship to the program is just columns — their role on
 * the contract (team seat), the access they hold, and where it came from
 * ("Team role", "Granted directly", "Via <org unit>", "Delegation"). Groups
 * with grants appear as rows too. Row click opens the §2C side panel: the
 * team seat is editable in place, and (for admins) every grant is listed
 * with Delegate/Revoke. No roster-vs-non-roster vocabulary anywhere — that
 * distinction confused its own engineers.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Label, SpecraButton, SpecraCombobox, SpecraOverlayDrawer, SpecraOverlayFooter, SpecraSectionHeader } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../../../contexts/authContext';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { authClient } from '../../../../lib/connect/clients';
import { GrantAssignmentDialog } from '../../../Admin/Access/GrantAssignmentDialog';
import { useListBundles } from '../../../Admin/Access/hooks';
import { RevokeConfirmDialog } from '../../../Admin/Access/RevokeConfirmDialog';
import { ResolvedId, useAdminNames } from '../../../Admin/nameResolution';
import { ConfirmDeleteDialog } from '../../_shared/components/ConfirmDeleteDialog';
import { DrawerDocumentsSection } from '../../_shared/components/DrawerDocumentsSection';
import { SectionNote } from '../../_shared/components/SectionNote';
import { isInternalContact } from '../../_shared/constants/contactTypes';
import { useTenantId } from '../../_shared/hooks/useTenantId';
import { contactHooks } from '../../Registries/hooks';
import { PROGRAM_KEY, useProgram } from '../hooks';
import { PROGRAM_WRITE_DENIED, useProgramWriteAccess } from '../programWriteAccess';
import { useTenantEnumValues } from '../referenceDataHooks';
import { programRoleAssignmentHooks } from '../relationshipHooks';
import { mergePeopleForPicker, parsePersonKey } from './personPicker';
import { GRANT_SOURCE_LABEL, grantSource, SCOPE_GRANTS_KEY, useProgramGrants } from './useProgramGrants';
/** §4 status badge: rectangular, 3px radius, tinted — never a pill. */ function AccessBadge({ mode }) {
    const tint = mode === 'full' ? 'bg-success-muted text-success' : mode === 'action_blocked' ? 'bg-muted text-muted-foreground' : 'bg-warning-muted text-warning';
    const label = mode === 'full' ? 'Full' : mode === 'simplified' ? 'Simplified' : mode === 'action_blocked' ? 'Read-only' : 'Masked';
    return /*#__PURE__*/ _jsx("span", {
        className: `inline-block rounded-[var(--radius-badge)] px-[7px] py-[var(--space-0-5)] text-[length:var(--fs-ui-label)] font-medium tracking-[0.04em] ${tint}`,
        children: label
    });
}
export function ProgramRolesTab({ programId }) {
    const { canEdit } = useProgramContext();
    // UpsertProgramRoleAssignment: update + area:programs. canEdit is update
    // alone; Remove still uses the server-computed manage_members flag.
    const canWrite = useProgramWriteAccess(programId).roster;
    const { user } = useAuth();
    const isAdmin = [
        'admin',
        'internal'
    ].includes(user?.role?.toLowerCase() ?? '');
    const tenantId = useTenantId();
    // Role names may be scoped to a business unit, so the picker is scoped to
    // this program's. The filter is NULL-or-match server-side: the BU's
    // programs see the company-wide names plus that BU's own. A program with
    // no BU passes nothing and narrows to the company-wide names below.
    const { data: prog } = useProgram(programId);
    const roleTypes = useTenantEnumValues('role_type', prog?.program?.businessUnitId);
    const programBusinessUnitId = prog?.program?.businessUnitId ?? '';
    // Admin-only catalog (AdminListBundles requires baseline admin/internal
    // role — manage_members doesn't clear that gate). Only the isAdmin-gated
    // Delegate/Revoke panel and the seat-role preview text below read it, so a
    // Program Lead/VP without baseline admin never needs it — fetching
    // unconditionally used to 403 on every load for exactly the personas
    // SAD-99 gates this tab for.
    const { data: bundles } = useListBundles(true, {
        enabled: isAdmin
    });
    // NULL-or-match server-side (SAD-75), same as the role-type filter above: a
    // BU-scoped program's Add-to-Team picker offers that BU's own contacts plus
    // every unscoped one, never a narrow-only filter. A BU-less program passes
    // '' and gets every contact, unchanged from before this filter existed.
    const contactsQuery = contactHooks.useList({
        companyId: tenantId,
        businessUnitId: programBusinessUnitId
    });
    const { data: rosterData, isLoading: rosterLoading } = programRoleAssignmentHooks.useList({
        programId
    });
    const upsert = programRoleAssignmentHooks.useUpsert();
    const remove = programRoleAssignmentHooks.useDelete();
    // manage_members (program lead / delivery VP) — server-computed (SAD-99),
    // never re-implemented here. Gates the Access/How columns and Remove,
    // separately from the roster write gate (update + area:programs).
    const canManageMembers = prog?.canManageMembers ?? false;
    const grants = useProgramGrants(programId, canManageMembers);
    const qc = useQueryClient();
    // A seat mutation's consequences live OUTSIDE the roster's own cache: the
    // grant it minted/revoked (SCOPE_GRANTS_KEY) and the server-derived
    // needsProgramLead flag on the program itself. Without these the banner
    // and the Access column report the pre-mutation world until a remount.
    const invalidateSeatConsequences = ()=>{
        void qc.invalidateQueries({
            queryKey: [
                SCOPE_GRANTS_KEY
            ]
        });
        void qc.invalidateQueries({
            queryKey: [
                PROGRAM_KEY,
                'detail',
                programId
            ]
        });
    };
    const [panelFor, setPanelFor] = useState(null);
    // Snapshot the last open selection so the drawer's exit animation keeps
    // showing the person instead of an emptied shell (panelFor nulls on close).
    const lastPanelFor = useRef(panelFor);
    if (panelFor) lastPanelFor.current = panelFor;
    const panelView = panelFor ?? lastPanelFor.current;
    const [deleting, setDeleting] = useState(null);
    const [revoking, setRevoking] = useState(null);
    const [delegating, setDelegating] = useState(null);
    const [contactId, setContactId] = useState('');
    // A platform user with no contact yet, picked straight from the tenant
    // directory (SAD-832). Mutually exclusive with contactId — picking one
    // clears the other.
    const [pickedUserId, setPickedUserId] = useState('');
    const [roleTypeId, setRoleTypeId] = useState('');
    const [isPrimary, setIsPrimary] = useState(true);
    const contacts = contactsQuery.data?.items ?? [];
    const rosterRows = rosterData?.items ?? [];
    // The people merge below joins rosterRows/grants.rows against contacts
    // (via userIdByContact) — if contacts hasn't resolved yet relative to the
    // other two, a seated user's own live grant keys under a contact-only
    // fallback instead of their userId and renders as a false "Access
    // pending" (or a duplicate ghost row) until contacts catches up. Hold the
    // merge in the table's existing loading treatment until all three have
    // settled, rather than computing it from a partial join.
    const isLoading = rosterLoading || grants.isLoading || contactsQuery.isLoading;
    // The role_bundle_key column carries the roster→grant mapping (metadata's
    // bundle_key survives only for rows predating the 000081 backfill); the
    // team seat's Access renders what holding that role MEANS.
    const bundleKeyByRoleType = useMemo(()=>{
        const m = new Map();
        for (const v of roleTypes.data ?? []){
            let key = v.roleBundleKey;
            if (!key && v.metadataJson) {
                try {
                    key = JSON.parse(v.metadataJson).bundle_key ?? '';
                } catch  {
                // roster-only on bad metadata
                }
            }
            if (key) m.set(v.id, key);
        }
        return m;
    }, [
        roleTypes.data
    ]);
    const bundleByKey = useMemo(()=>new Map((bundles ?? []).map((b)=>[
                b.key,
                b
            ])), [
        bundles
    ]);
    // WS6: the stable contact↔user link is the join between the team list
    // (contact-based) and grants (user-based).
    const userIdByContact = useMemo(()=>new Map(contacts.filter((c)=>c.platformUserId).map((c)=>[
                c.id,
                c.platformUserId
            ])), [
        contacts
    ]);
    const people = useMemo(()=>{
        if (isLoading) return [];
        const byKey = new Map();
        for (const r of rosterRows){
            const userId = userIdByContact.get(r.contactId) || undefined;
            // Dedup by linked user, else by contact; a row with neither (legacy
            // data) stays its own row rather than collapsing into others.
            const key = userId ?? (r.contactId ? `contact:${r.contactId}` : `roster:${r.id}`);
            byKey.set(key, {
                key,
                kind: 'person',
                name: r.contactDisplayName || r.contactEmail || 'Unnamed contact',
                email: r.contactEmail,
                userId,
                roster: r,
                grants: []
            });
        }
        for (const g of grants.rows){
            const a = g.assignment;
            if (a.groupId) {
                const key = `group:${a.groupId}`;
                const entry = byKey.get(key) ?? {
                    key,
                    kind: 'group',
                    name: a.groupId,
                    groupId: a.groupId,
                    grants: []
                };
                entry.grants.push(g);
                byKey.set(key, entry);
                continue;
            }
            const entry = byKey.get(a.userId) ?? {
                key: a.userId,
                kind: 'person',
                name: a.userId,
                userId: a.userId,
                grants: []
            };
            entry.grants.push(g);
            byKey.set(a.userId, entry);
        }
        // Team seats first, then people with only grants, groups last; stable by name.
        return [
            ...byKey.values()
        ].sort((a, b)=>Number(!a.roster) - Number(!b.roster) || Number(a.kind === 'group') - Number(b.kind === 'group') || a.name.localeCompare(b.name));
    }, [
        isLoading,
        rosterRows,
        grants.rows,
        userIdByContact
    ]);
    // Same admin-catalog gate as bundles above (useAdminNames pulls the group
    // and scope-tree catalogs plus ResolveUserNames, all baseline-admin-only):
    // the rows it names only ever exist via grants.rows, which is itself empty
    // whenever canManageMembers is false, so a non-admin gets nothing to name
    // regardless — just without the guaranteed 403 on every load.
    const names = useAdminNames([
        ...people.filter((p)=>p.kind === 'person' && !p.roster && p.userId).map((p)=>p.userId ?? ''),
        ...grants.rows.map((g)=>g.assignment.originalOwnerUserId).filter(Boolean)
    ], {
        enabled: isAdmin
    });
    // Team members resolve from the roster; everyone else from the directory.
    const teamNameByUserId = useMemo(()=>new Map(people.filter((p)=>p.userId && p.roster).map((p)=>[
                p.userId,
                p.name
            ])), [
        people
    ]);
    const userLabel = (id)=>teamNameByUserId.get(id) ?? names.user(id) ?? grants.userName(id) ?? (/^[0-9a-f]{32}$/.test(id) ? `${id.slice(0, 8)}…` : id);
    /**
   * A row's displayable name — team seats already carry one; a group or a
   * direct-grant person (no roster seat) resolves through the same chain as
   * `userLabel`/`names.group` (admin resolver, then ryugu's server-resolved
   * grant name, then the raw id) so the drawer title never shows a hex id
   * the inline table cell has already resolved.
   */ const personDisplayName = (p)=>p.kind === 'group' ? names.group(p.groupId ?? '') ?? grants.groupName(p.groupId ?? '') ?? p.name : p.roster ? p.name : userLabel(p.userId ?? p.key);
    /** One grant's provenance line — designees name the owner they cover for. */ const grantLabel = (g)=>{
        const a = g.assignment;
        const src = grantSource(a);
        if (src === 'delegation' && a.originalOwnerUserId) {
            return `Backing up ${userLabel(a.originalOwnerUserId)}`;
        }
        return g.inherited ? `Via ${grants.nodeName(g.grantedOnNodeId)}` : GRANT_SOURCE_LABEL[src];
    };
    // The Add-to-Team picker hides anyone already on this page (the backend
    // additionally rejects an exact duplicate seat — this is just honest UX).
    const seatedUsers = useMemo(()=>new Set(rosterRows.map((r)=>userIdByContact.get(r.contactId)).filter(Boolean)), [
        rosterRows,
        userIdByContact
    ]);
    const availableContacts = useMemo(()=>{
        const seatedContacts = new Set(rosterRows.map((r)=>r.contactId).filter(Boolean));
        // Team seats are internal-team assignments (main #925): only INTERNAL
        // registry contacts are offered.
        return contacts.filter((c)=>isInternalContact(c) && !seatedContacts.has(c.id) && !(c.platformUserId && seatedUsers.has(c.platformUserId)));
    }, [
        contacts,
        rosterRows,
        seatedUsers
    ]);
    // SAD-832: the tenant's platform user directory, same source
    // PlatformUserPicker.tsx reads (mariejois's ListUsersForSharing via
    // sabaody — member-accessible, unlike Admin/People's admin-only
    // UserManagementService). Gated to when the picker is actually open; a
    // just-invited member with no contact yet now shows up here directly.
    const directoryUsersQuery = useQuery({
        queryKey: [
            'delivery:program-role-picker-directory',
            tenantId
        ],
        enabled: panelFor === 'new',
        queryFn: async ()=>{
            const resp = await authClient.listUsersForSharing({
                query: ''
            });
            return resp.users.map((u)=>({
                    userId: u.id,
                    name: u.name,
                    email: u.email
                }));
        }
    });
    const availablePeople = useMemo(()=>mergePeopleForPicker(availableContacts, (directoryUsersQuery.data ?? []).filter((u)=>!seatedUsers.has(u.userId))), [
        availableContacts,
        directoryUsersQuery.data,
        seatedUsers
    ]);
    // Live grants only (SAD-99): this used to also seed from rosterBundle(p.roster)
    // — the role type's INTENDED bundle mapping — which is why a roster row with
    // NO live authz.role_assignment still showed "Full — Program Lead". Display
    // data comes straight off the grant (ryugu resolves it server-side), not the
    // admin-only bundle catalog non-admin callers can't read anyway.
    const heldBundles = (p)=>{
        const out = new Map();
        for (const g of p.grants){
            const a = g.assignment;
            if (!out.has(a.roleBundleKey)) {
                out.set(a.roleBundleKey, {
                    key: a.roleBundleKey,
                    displayName: a.roleBundleDisplayName || a.roleBundleKey,
                    defaultViewMode: a.defaultViewMode
                });
            }
        }
        return [
            ...out.values()
        ];
    };
    // Whether this roster seat's role type IS mapped to a bundle (roster→grant
    // mapping, role_bundle_key column) — a plain tenant-enum-values lookup, not
    // the admin-only bundle catalog, so it's readable by a Program Lead too.
    // Used only to tell "access pending" (mapped, but no live grant yet) apart
    // from "None" (a genuinely roster-only role, mapped to nothing).
    const rosterMapsToBundle = (r)=>!!bundleKeyByRoleType.get(r.roleTypeId);
    const sourceSummary = (p)=>{
        const parts = new Set();
        if (p.roster) parts.add('Team role');
        for (const g of p.grants){
            if (grantSource(g.assignment) === 'team-role') continue; // already covered by the seat
            parts.add(grantLabel(g));
        }
        return [
            ...parts
        ].join(' · ') || '—';
    };
    const openPanel = (p)=>{
        const roster = p === 'new' ? null : p.roster;
        setContactId(roster?.contactId ?? '');
        setPickedUserId('');
        setRoleTypeId(roster?.roleTypeId ?? '');
        setIsPrimary(roster?.isPrimary ?? true);
        setPanelFor(p);
    };
    const submitSeat = async ()=>{
        if (!contactId && !pickedUserId || !roleTypeId) return;
        const editing = panelFor !== 'new' && panelFor ? panelFor.roster : null;
        const resp = await upsert.mutateAsync({
            id: editing?.id ?? '',
            programId,
            contactId,
            // SAD-832: a contactless platform user picked directly from the
            // directory — ryugu resolves/creates the backing contact server-side.
            userId: contactId ? undefined : pickedUserId,
            roleTypeId,
            isPrimary
        });
        switch(resp?.accessState){
            case 'active':
                toast.success('Role assigned — system access granted');
                break;
            case 'no_account':
                toast.info('Role recorded. This contact has no user account, so no access was granted.');
                break;
            case 'requires_admin':
                toast.info('Role recorded. Granting access requires an authorized access administrator.');
                break;
            case 'grant_pending':
                toast.warning('Role recorded, but granting access failed — re-save to retry.');
                break;
            default:
                break;
        }
        invalidateSeatConsequences();
        setPanelFor(null);
    };
    // The picked contact's registry-profile role (new-seat flow): non-empty
    // locks the role picker (main #925) — a dead id still locks, with an
    // honest message instead of the raw UUID. On EDIT the picker is
    // deliberately free (per #925 + product decision): the registry card is
    // the org-wide DEFAULT, the seat is the per-contract truth — one person
    // can legitimately hold different roles on different contracts. When the
    // seat diverges from the card we say so instead of hiding it.
    const panelPerson = panelView !== 'new' ? panelView : null;
    const pickedContact = contacts.find((c)=>c.id === contactId);
    const pickedRegistryRole = panelView === 'new' ? pickedContact?.roleTypeId ?? '' : '';
    const editContactRegistryRole = panelView !== 'new' && panelPerson?.roster ? contacts.find((c)=>c.id === panelPerson.roster?.contactId)?.roleTypeId ?? '' : '';
    const registryRoleLabel = (id)=>(roleTypes.data ?? []).find((v)=>v.id === id)?.displayLabel;
    // ACC-03: the PM/escalation/quality notification audiences resolve from
    // this roster, and a recipient row whose contact has no platform_user_id
    // never reaches a bell — the routing silently no-ops. Warn at the moment
    // the person is chosen, not after the save. An unresolved contact is
    // unknown, not unlinked (same rule as the access column above).
    const pickedContactUnlinked = !!pickedContact && !pickedContact.platformUserId;
    // A program with NO business unit is offered exactly the names offered
    // everywhere. Asking the server for no BU means no BU narrowing at all —
    // right for /admin/permissions, wrong here: a name scoped to one BU is by
    // definition not offered to a program outside it, and the dedup keying on
    // (code, business_unit_id) means the scoped copy arrives ALONGSIDE the
    // seeded row it overrides — the same role name would show twice in this
    // picker. Narrowing at the consumer keeps the shared hook's unscoped
    // query key intact for callers (registry tabs) that want every row.
    const roleTypeOptions = useMemo(()=>(roleTypes.data ?? []).filter((v)=>programBusinessUnitId !== '' || !v.businessUnitId), [
        roleTypes.data,
        programBusinessUnitId
    ]);
    const teamCount = rosterRows.length;
    const othersCount = people.length - people.filter((p)=>p.roster).length;
    // A program with no lead cannot be worked day-to-day: naming the lead is
    // the Delivery VP's move and everything else waits on it, so it goes at
    // the top of the section rather than reading as one missing row among
    // many. The flag is derived server-side from the assignments (no stored
    // status), and is false when the tenant has no role mapped to the lead
    // bundle — so this banner appears only when the lead seat is genuinely
    // identifiable and empty.
    const needsLead = prog?.program?.needsProgramLead ?? false;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        children: [
            needsLead && /*#__PURE__*/ _jsxs("div", {
                className: "rounded-md border-l-2 border-warning bg-warning-muted px-3 py-2 text-sm text-foreground",
                "data-testid": Testids.deliveryProgramRel.rolesNeedsLeadBanner,
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "font-medium",
                        children: "This program has no program lead."
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: "A Delivery VP needs to add a program lead here before the rest of the program can be staffed — the lead staffs every other role."
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SectionNote, {
                children: "Roles are the Sprezz team assigned to this program. Government and partner points of contact are recorded on the Contacts tab."
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-end justify-between gap-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("h1", {
                                className: "font-display text-[length:var(--fs-ui-home-title)] font-medium tracking-[-0.01em]",
                                children: "Team & Access"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                                children: people.length === 0 ? 'No one is involved with this program yet.' : `${teamCount} on the team` + (othersCount > 0 ? ` · ${othersCount} more with access` : '') + (grants.orgWideCount > 0 ? ` · ${grants.orgWideCount} org-wide grant${grants.orgWideCount === 1 ? '' : 's'} also reach here` : '')
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>openPanel('new'),
                        disabled: !canWrite,
                        title: !canWrite ? PROGRAM_WRITE_DENIED.roster : undefined,
                        "data-testid": Testids.deliveryProgramRel.roleCreate,
                        children: "Add to Team"
                    })
                ]
            }),
            people.length === 0 && !isLoading ? /* §4 empty state: what's missing, why it matters, one action. */ /*#__PURE__*/ _jsxs("div", {
                className: "py-10 text-center",
                "data-testid": Testids.pageState.empty,
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-[length:var(--fs-ui-body)]",
                        children: "No one is involved with this program."
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "mt-1 text-[length:var(--fs-ui-meta)] text-muted-foreground",
                        children: "Add the team — roles like Program Lead grant system access automatically."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        className: "mt-4",
                        onClick: ()=>openPanel('new'),
                        disabled: !canWrite,
                        title: !canWrite ? PROGRAM_WRITE_DENIED.roster : undefined,
                        children: "Add the First Person"
                    })
                ]
            }) : /*#__PURE__*/ _jsxs("table", {
                className: "w-full",
                "data-testid": Testids.deliveryProgramRel.rolesTable,
                children: [
                    /*#__PURE__*/ _jsx("thead", {
                        children: /*#__PURE__*/ _jsx("tr", {
                            className: "border-b border-border text-left",
                            children: [
                                'Name',
                                'Role on Contract',
                                ...canManageMembers ? [
                                    'Access',
                                    'How'
                                ] : [],
                                'Email',
                                ''
                            ].map((h, i)=>/*#__PURE__*/ _jsx("th", {
                                    className: "pb-2 pr-2 text-[length:var(--fs-ui-eyebrow)] font-medium uppercase tracking-[0.06em] text-muted-foreground first:pl-2",
                                    children: h
                                }, `${h}-${i}`))
                        })
                    }),
                    /*#__PURE__*/ _jsx("tbody", {
                        children: people.map((p)=>{
                            const held = heldBundles(p);
                            const first = held[0];
                            return /*#__PURE__*/ _jsxs("tr", {
                                "data-testid": Testids.deliveryProgramRel.roleRow,
                                "data-row-id": p.roster?.id ?? p.key,
                                ...held.length > 0 ? {
                                    'data-access-keys': held.map((h)=>h.key).join(' ')
                                } : {},
                                className: "cursor-pointer border-b border-border/40 text-[length:var(--fs-ui-body)] hover:bg-muted/30",
                                onClick: ()=>openPanel(p),
                                children: [
                                    /*#__PURE__*/ _jsx("td", {
                                        className: "py-[11px] pl-2 pr-2 font-medium",
                                        children: p.kind === 'group' ? /*#__PURE__*/ _jsxs("span", {
                                            className: "inline-flex items-center gap-1.5",
                                            children: [
                                                /*#__PURE__*/ _jsx(Users, {
                                                    className: "h-3.5 w-3.5 text-muted-foreground"
                                                }),
                                                personDisplayName(p)
                                            ]
                                        }) : p.roster ? p.name : /*#__PURE__*/ _jsx(ResolvedId, {
                                            id: p.userId ?? p.key,
                                            name: names.user(p.userId ?? '') ?? grants.userName(p.userId ?? '')
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("td", {
                                        className: "py-[11px] pr-2",
                                        children: p.roster ? /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                p.roster.roleTypeLabel || p.roster.roleTypeCode,
                                                !p.roster.isPrimary && /*#__PURE__*/ _jsx("span", {
                                                    className: "ml-1.5 rounded-[var(--radius-badge)] bg-muted px-[7px] py-[var(--space-0-5)] text-[length:var(--fs-ui-label)] text-muted-foreground",
                                                    title: "Backup for this role — the primary holder is another team member",
                                                    children: "backup"
                                                })
                                            ]
                                        }) : /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: "—"
                                        })
                                    }),
                                    canManageMembers && /*#__PURE__*/ _jsx("td", {
                                        className: "py-[11px] pr-2",
                                        children: first ? /*#__PURE__*/ _jsxs("span", {
                                            className: "inline-flex items-center gap-1.5",
                                            "data-testid": Testids.deliveryProgramRel.roleAccessChip,
                                            "data-access": first.key,
                                            children: [
                                                /*#__PURE__*/ _jsx(AccessBadge, {
                                                    mode: first.defaultViewMode
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "text-[length:var(--fs-ui-body)]",
                                                    children: first.displayName
                                                }),
                                                held.length > 1 && /*#__PURE__*/ _jsxs("span", {
                                                    className: "text-[length:var(--fs-ui-label)] text-muted-foreground",
                                                    children: [
                                                        "+",
                                                        held.length - 1,
                                                        " more"
                                                    ]
                                                })
                                            ]
                                        }) : p.roster && !p.userId && // An unresolved contact (list still loading, or a row
                                        // pointing at a contact this caller can't see) is
                                        // unknown, not access-less — claiming "no platform
                                        // access" there would be a guess dressed as a fact.
                                        contacts.some((c)=>c.id === p.roster?.contactId) ? // "They're in the org chart" and "they have an
                                        // account" are different claims — spell this one out
                                        // in words, not a colour or an icon.
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                            "data-testid": Testids.deliveryProgramRel.roleNoPlatformAccess,
                                            "data-access": "roster-only",
                                            title: "Access starts when they get a user account",
                                            children: "Contact only — no platform access"
                                        }) : p.roster && rosterMapsToBundle(p.roster) && // Same resolvability requirement as the contact-only
                                        // branch above: a seat whose contact can't be joined
                                        // to a grant is unknown, not pending.
                                        contacts.some((c)=>c.id === p.roster?.contactId) ? // Mapped to a bundle but no live grant exists yet — a
                                        // chip claiming access that isn't there is the exact
                                        // defect this surface shipped with (SAD-99); this
                                        // must read as visibly different from a live grant.
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-[length:var(--fs-ui-meta)] text-warning",
                                            "data-testid": Testids.deliveryProgramRel.roleAccessChip,
                                            "data-access": "pending",
                                            title: "This role should grant system access, but no live grant exists yet — re-save the seat, or ask an access administrator to complete it.",
                                            children: "Access pending"
                                        }) : /*#__PURE__*/ _jsx("span", {
                                            className: "text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                            "data-testid": Testids.deliveryProgramRel.roleAccessChip,
                                            "data-access": "roster-only",
                                            title: "This role is tracked only; it grants no system access",
                                            children: "None"
                                        })
                                    }),
                                    canManageMembers && /*#__PURE__*/ _jsx("td", {
                                        className: "py-[11px] pr-2 text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                        title: "Where their access comes from — a team role grants automatically; the rest were granted in the admin console, inherited from an org unit above, or delegated",
                                        children: sourceSummary(p)
                                    }),
                                    /*#__PURE__*/ _jsx("td", {
                                        className: "py-[11px] pr-2 text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                        children: p.email ?? '—'
                                    }),
                                    /*#__PURE__*/ _jsx("td", {
                                        className: "py-[11px] pr-2 text-right",
                                        children: canManageMembers && p.roster && /*#__PURE__*/ _jsx(SpecraButton, {
                                            variant: "ghost",
                                            size: "sm",
                                            className: "text-[length:var(--fs-ui-meta)] text-danger hover:bg-danger-muted hover:text-danger",
                                            onClick: (e)=>{
                                                e.stopPropagation();
                                                setDeleting(p.roster ?? null);
                                            },
                                            children: "Remove"
                                        })
                                    })
                                ]
                            }, p.key);
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
                open: !!panelFor,
                onOpenChange: (next)=>{
                    if (!next) setPanelFor(null);
                },
                title: panelView === 'new' ? 'Add to Team' : panelPerson ? personDisplayName(panelPerson) : '',
                description: panelPerson?.email,
                testId: Testids.deliveryProgramRel.rolePanel,
                footer: /*#__PURE__*/ _jsx(SpecraOverlayFooter, {
                    leading: canManageMembers && panelPerson?.roster ? /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "destructive",
                        className: "text-[length:var(--fs-ui-meta)]",
                        onClick: ()=>{
                            setPanelFor(null);
                            setDeleting(panelPerson.roster ?? null);
                        },
                        children: "Remove From Team"
                    }) : undefined,
                    cancelLabel: panelView === 'new' || panelPerson?.roster ? 'Cancel' : 'Close',
                    onCancel: ()=>setPanelFor(null),
                    confirmLabel: panelView === 'new' ? 'Add' : 'Save',
                    onConfirm: panelView === 'new' || panelPerson?.roster ? ()=>void submitSeat() : undefined,
                    confirmProps: panelView === 'new' || panelPerson?.roster ? {
                        disabled: !canWrite || !contactId && !pickedUserId || !roleTypeId || upsert.isPending,
                        'data-testid': Testids.deliveryProgramRel.rolePanelSave
                    } : undefined
                }),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        panelPerson?.kind === 'group' ? /*#__PURE__*/ _jsx("p", {
                            className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                            children: "A group — everyone in it inherits the grants below. Manage its members under Admin → Groups."
                        }) : /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-4",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                    variant: "group",
                                    title: "On the Team"
                                }),
                                panelView === 'new' && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            children: "Person"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraCombobox, {
                                            label: "Person",
                                            options: availablePeople.map((p)=>({
                                                    value: p.key,
                                                    label: p.label
                                                })),
                                            value: contactId ? `contact:${contactId}` : pickedUserId ? `user:${pickedUserId}` : undefined,
                                            placeholder: "Pick from your contacts or your organization",
                                            searchPlaceholder: "Search people by name or email…",
                                            emptyMessage: "No one matches.",
                                            triggerProps: {
                                                'data-testid': Testids.deliveryProgramRel.rolePanelContact
                                            },
                                            onValueChange: (v)=>{
                                                const parsed = v ? parsePersonKey(v) : null;
                                                if (parsed?.kind === 'contact') {
                                                    setContactId(parsed.id);
                                                    setPickedUserId('');
                                                    // The registry profile's role travels with the pick
                                                    // (and clears on a switch to a role-less contact) —
                                                    // main #925 semantics, server-enforced.
                                                    setRoleTypeId(contacts.find((c)=>c.id === parsed.id)?.roleTypeId ?? '');
                                                } else if (parsed?.kind === 'user') {
                                                    // A platform user with no contact yet (SAD-832) has no
                                                    // registry profile to inherit a role from.
                                                    setPickedUserId(parsed.id);
                                                    setContactId('');
                                                    setRoleTypeId('');
                                                }
                                            }
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-[length:var(--fs-ui-label)] text-muted-foreground",
                                            children: availablePeople.length === 0 ? 'Everyone in your organization is already on this team.' : 'People already on the team are hidden. Not here? Invite them under Admin → People.'
                                        })
                                    ]
                                }),
                                pickedContactUnlinked && /*#__PURE__*/ _jsx("p", {
                                    className: "text-[length:var(--fs-ui-label)] text-warning",
                                    "data-testid": Testids.deliveryProgramRel.rolePanelNoNotificationsNote,
                                    children: "This person will not receive in-app notifications — their contact has no linked platform account. Link one in Registries → Contacts."
                                }),
                                (panelView === 'new' || panelPerson?.roster) && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "space-y-1.5",
                                            children: [
                                                /*#__PURE__*/ _jsx(Label, {
                                                    children: "Role on This Contract"
                                                }),
                                                pickedRegistryRole !== '' ? /*#__PURE__*/ _jsxs(_Fragment, {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("div", {
                                                            className: "rounded-md border bg-muted px-3 py-2 text-[length:var(--fs-ui-body)]",
                                                            "data-testid": Testids.deliveryProgramRel.roleTypeLocked,
                                                            children: (roleTypes.data ?? []).find((v)=>v.id === pickedRegistryRole)?.displayLabel ?? 'Role no longer available — update the registry profile.'
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "text-[length:var(--fs-ui-label)] text-muted-foreground",
                                                            children: "Set on their registry profile and fixed here — change it in Registries → Contacts."
                                                        })
                                                    ]
                                                }) : /*#__PURE__*/ _jsx(SpecraCombobox, {
                                                    label: "Role",
                                                    options: roleTypeOptions.map((v)=>({
                                                            value: v.id,
                                                            label: v.displayLabel
                                                        })),
                                                    value: roleTypeId || undefined,
                                                    onValueChange: (v)=>setRoleTypeId(v ?? ''),
                                                    disabled: !canWrite,
                                                    placeholder: "Pick a role",
                                                    searchPlaceholder: "Search roles…",
                                                    triggerProps: {
                                                        'data-testid': Testids.deliveryProgramRel.rolePanelRoleType
                                                    }
                                                }),
                                                /*#__PURE__*/ _jsx("p", {
                                                    className: "text-[length:var(--fs-ui-label)] text-muted-foreground",
                                                    children: "Your organization's role list lives under Delivery → Registries → Program Roles."
                                                }),
                                                editContactRegistryRole !== '' && roleTypeId !== editContactRegistryRole && /*#__PURE__*/ _jsxs("p", {
                                                    className: "text-[length:var(--fs-ui-label)] text-warning",
                                                    "data-testid": Testids.deliveryProgramRel.roleDivergesFromRegistry,
                                                    children: [
                                                        "Their registry profile says",
                                                        ' ',
                                                        registryRoleLabel(editContactRegistryRole) ?? 'a different role',
                                                        " — this seat can differ per contract."
                                                    ]
                                                }),
                                                roleTypeId && (()=>{
                                                    const bundleKey = bundleKeyByRoleType.get(roleTypeId) ?? '';
                                                    const bundle = bundleByKey.get(bundleKey);
                                                    return /*#__PURE__*/ _jsx("p", {
                                                        className: "text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                                        children: bundleKey ? `Holding this role grants ${bundle ? `“${bundle.displayName}” ` : ''}access on this program.` : 'This role is tracked on the team list only — it grants no system access.'
                                                    });
                                                })()
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("label", {
                                            className: "flex cursor-pointer items-center gap-2 text-[length:var(--fs-ui-body)]",
                                            children: [
                                                /*#__PURE__*/ _jsx("input", {
                                                    type: "checkbox",
                                                    checked: !isPrimary,
                                                    disabled: !canWrite,
                                                    onChange: (e)=>setIsPrimary(!e.target.checked)
                                                }),
                                                "Backup for this role (someone else is the primary holder)"
                                            ]
                                        })
                                    ]
                                }),
                                panelView !== 'new' && !panelPerson?.roster && /*#__PURE__*/ _jsx("p", {
                                    className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                                    children: "Not on the team — they hold access without a team seat. Use “Add to Team” from the page header to give them one."
                                })
                            ]
                        }),
                        isAdmin && panelPerson && panelPerson.grants.length > 0 && /*#__PURE__*/ _jsxs("div", {
                            className: "mt-[var(--space-5-5)]",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                    variant: "group",
                                    title: "Access on This Program"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "mt-2 space-y-2",
                                    children: panelPerson.grants.map((g)=>{
                                        const a = g.assignment;
                                        const b = bundleByKey.get(a.roleBundleKey);
                                        const src = grantSource(a);
                                        return /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center justify-between gap-2 rounded-md border px-3 py-2",
                                            "data-testid": Testids.deliveryProgramRel.accessGrantRow,
                                            "data-grant-id": a.id,
                                            "data-provenance": src,
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "min-w-0 text-[length:var(--fs-ui-body)]",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex items-center gap-1.5",
                                                            children: [
                                                                b && /*#__PURE__*/ _jsx(AccessBadge, {
                                                                    mode: b.defaultViewMode
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "font-medium",
                                                                    children: b?.displayName ?? a.roleBundleKey
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                                            children: grantLabel(g)
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex shrink-0 gap-1",
                                                    children: [
                                                        a.userId && a.assignmentType !== 'designee' && canEdit && /*#__PURE__*/ _jsx(SpecraButton, {
                                                            variant: "ghost",
                                                            size: "sm",
                                                            className: "text-[length:var(--fs-ui-meta)]",
                                                            "data-testid": Testids.deliveryProgramRel.accessGrantDelegate,
                                                            title: "Time-boxed designee grant of this role at this scope",
                                                            onClick: ()=>setDelegating({
                                                                    ownerUserId: a.userId,
                                                                    roleBundleKey: a.roleBundleKey,
                                                                    scopeNodeId: a.scopeNodeId
                                                                }),
                                                            children: "Delegate…"
                                                        }),
                                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                                            variant: "destructive",
                                                            size: "sm",
                                                            className: "text-[length:var(--fs-ui-meta)]",
                                                            "data-testid": Testids.deliveryProgramRel.accessGrantRevoke,
                                                            disabled: src === 'team-role' || !canEdit,
                                                            title: src === 'team-role' ? 'Comes from their team role — change or remove the seat instead' : undefined,
                                                            onClick: ()=>setRevoking(a),
                                                            children: "Revoke"
                                                        })
                                                    ]
                                                })
                                            ]
                                        }, a.id);
                                    })
                                })
                            ]
                        }),
                        panelPerson?.kind === 'person' && panelPerson.roster?.contactId && /*#__PURE__*/ _jsx("div", {
                            className: "mt-[var(--space-5-5)]",
                            children: /*#__PURE__*/ _jsx(DrawerDocumentsSection, {
                                programId: programId,
                                scope: {
                                    contactId: panelPerson.roster.contactId
                                },
                                emptyText: "No documents for this person yet.",
                                canEdit: canEdit
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Remove From Team",
                description: `${deleting?.contactDisplayName || 'This person'} comes off the team. Access granted by their role is revoked; access granted separately is untouched.`,
                onConfirm: async ()=>{
                    if (!deleting) return;
                    await remove.mutateAsync(deleting.id);
                    invalidateSeatConsequences();
                    setDeleting(null);
                },
                isPending: remove.isPending
            }),
            revoking && /*#__PURE__*/ _jsx(RevokeConfirmDialog, {
                open: true,
                onOpenChange: (o)=>!o && setRevoking(null),
                assignmentId: revoking.id,
                userId: revoking.userId,
                bundleDisplayName: bundleByKey.get(revoking.roleBundleKey)?.displayName ?? revoking.roleBundleKey
            }),
            delegating && // Mounted only once opened: it unconditionally calls AdminListBundles
            // (fine on the admin-only pages it's shared with), and `delegating`
            // itself is only ever set from the isAdmin-gated Delegate… button —
            // an always-mounted instance fired that 403 for every Program
            // Lead/VP viewing this tab, admin surface or not.
            /*#__PURE__*/ _jsx(GrantAssignmentDialog, {
                open: true,
                onOpenChange: (o)=>!o && setDelegating(null),
                delegateFrom: delegating
            })
        ]
    });
}
